import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import {
  URGENCY,
  URGENCY_LABELS,
  URGENCY_COLORS,
} from '../shared/constants';

const useStyles = makeStyles(() => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  funnelContainer: { marginBottom: 24 },
  funnelStep: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  funnelBar: {
    height: 36,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: 12,
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.85rem',
    transition: 'width 0.4s ease',
    minWidth: 48,
  },
  funnelLabel: {
    width: 180,
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    flexShrink: 0,
  },
  funnelConversion: {
    fontSize: '0.75rem',
    color: '#777',
    marginLeft: 12,
    whiteSpace: 'nowrap',
  },
  waitTimeCard: {
    padding: 20,
    borderRadius: 8,
    textAlign: 'center',
    backgroundColor: '#f5f7fa',
    border: '1px solid #e0e0e0',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  waitTimeValue: { fontSize: '1.75rem', fontWeight: 700, color: '#3F51B5' },
  waitTimeLabel: { fontSize: '0.8rem', marginTop: 4, color: '#555' },
  summaryCard: {
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    border: '2px solid',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  summaryValue: { fontSize: '1.5rem', fontWeight: 700 },
  summaryLabel: { fontSize: '0.8rem', marginTop: 4, color: '#555' },
}));

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const FUNNEL_DATA = [
  { label: 'Referrals Received', count: 156, color: '#3F51B5' },
  { label: 'Scheduled', count: 128, color: '#2196F3' },
  { label: 'Pre-Op Complete', count: 118, color: '#00BCD4' },
  { label: 'Completed', count: 108, color: '#4CAF50' },
];

const WAIT_TIMES = [
  { label: 'Referral to Schedule', value: '4.2', unit: 'days' },
  { label: 'Schedule to Surgery', value: '6.8', unit: 'days' },
  { label: 'Total Referral to Surgery', value: '11.0', unit: 'days' },
];

const REFERRAL_SOURCES = [
  { department: 'Internal Medicine', doctor: 'Dr. Ssekitoleko Paul', referrals: 28 },
  { department: 'Obstetrics & Gynaecology', doctor: 'Dr. Namubiru Harriet', referrals: 24 },
  { department: 'Orthopaedics Clinic', doctor: 'Dr. Kato Samuel', referrals: 18 },
  { department: 'Urology Clinic', doctor: 'Dr. Lubega David', referrals: 15 },
  { department: 'ENT Clinic', doctor: 'Dr. Tumwine Patience', referrals: 12 },
];

const URGENCY_REFERRALS = [
  { urgency: URGENCY.ELECTIVE, count: 104 },
  { urgency: URGENCY.URGENT, count: 36 },
  { urgency: URGENCY.EMERGENCY, count: 16 },
];

const EXPIRED_CANCELLED = {
  expired: 8,
  cancelled: 12,
  reasons: [
    { reason: 'Patient declined surgery', count: 5 },
    { reason: 'Medical condition changed', count: 4 },
    { reason: 'Referral validity lapsed', count: 4 },
    { reason: 'Alternative treatment chosen', count: 3 },
    { reason: 'Patient relocated', count: 2 },
    { reason: 'Insurance not approved', count: 2 },
  ],
};

// ── Component ────────────────────────────────────────────────────────────────
const ReferralMetrics = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const maxFunnel = FUNNEL_DATA[0].count;

  return (
    <div>
      <PageTitle title="Referral Metrics" />
      <SubHeader title="Surgery" subTitle="Referral Metrics" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Date Range Filter ─────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </div>

        {/* ── Referral Funnel ───────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Referral Pipeline</Typography>
        <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
          <div className={classes.funnelContainer}>
            {FUNNEL_DATA.map((step, idx) => {
              const pct = maxFunnel > 0 ? (step.count / maxFunnel) * 100 : 0;
              const conversionRate = idx > 0
                ? Math.round((step.count / FUNNEL_DATA[idx - 1].count) * 100)
                : 100;
              return (
                <div key={step.label} className={classes.funnelStep}>
                  <Typography className={classes.funnelLabel}>{step.label}</Typography>
                  <div
                    className={classes.funnelBar}
                    style={{
                      width: `${Math.max(pct, 8)}%`,
                      backgroundColor: step.color,
                    }}
                  >
                    {step.count}
                  </div>
                  {idx > 0 && (
                    <Typography className={classes.funnelConversion}>
                      {conversionRate}% conversion
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
          <Typography style={{ fontSize: '0.8rem', color: '#777' }}>
            Overall conversion rate: {Math.round((FUNNEL_DATA[FUNNEL_DATA.length - 1].count / FUNNEL_DATA[0].count) * 100)}%
            ({FUNNEL_DATA[FUNNEL_DATA.length - 1].count} of {FUNNEL_DATA[0].count} referrals completed)
          </Typography>
        </Paper>

        {/* ── Average Wait Times ────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Average Wait Times</Typography>
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          {WAIT_TIMES.map((w) => (
            <Grid item xs={12} sm={4} key={w.label}>
              <Paper className={classes.waitTimeCard} elevation={0}>
                <Typography className={classes.waitTimeValue}>
                  {w.value} <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>{w.unit}</span>
                </Typography>
                <Typography className={classes.waitTimeLabel}>{w.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Top Referring Doctors ──────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Top Referring Doctors</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Department</TableCell>
                <TableCell align="center">Referrals</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {REFERRAL_SOURCES.map((src, idx) => (
                <TableRow key={src.doctor}>
                  <TableCell className={classes.tableCell}>{idx + 1}</TableCell>
                  <TableCell className={classes.tableCell}>{src.doctor}</TableCell>
                  <TableCell className={classes.tableCell}>{src.department}</TableCell>
                  <TableCell align="center" className={classes.tableCell}>{src.referrals}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Urgency Distribution ──────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Urgency Distribution</Typography>
        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          {URGENCY_REFERRALS.map((u) => (
            <Grid item xs={12} sm={4} key={u.urgency}>
              <Paper className={classes.summaryCard} elevation={0} style={{ borderColor: URGENCY_COLORS[u.urgency] }}>
                <Typography className={classes.summaryValue} style={{ color: URGENCY_COLORS[u.urgency] }}>
                  {u.count}
                </Typography>
                <Typography className={classes.summaryLabel}>{URGENCY_LABELS[u.urgency]}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Expired / Cancelled ───────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Expired & Cancelled Referrals</Typography>
        <Paper className={classes.sectionPaper} elevation={0} variant="outlined">
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={6} sm={3}>
              <div style={{ textAlign: 'center' }}>
                <Typography style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9E9E9E' }}>
                  {EXPIRED_CANCELLED.expired}
                </Typography>
                <Typography style={{ fontSize: '0.8rem', color: '#777' }}>Expired</Typography>
              </div>
            </Grid>
            <Grid item xs={6} sm={3}>
              <div style={{ textAlign: 'center' }}>
                <Typography style={{ fontSize: '1.5rem', fontWeight: 700, color: '#F44336' }}>
                  {EXPIRED_CANCELLED.cancelled}
                </Typography>
                <Typography style={{ fontSize: '0.8rem', color: '#777' }}>Cancelled</Typography>
              </div>
            </Grid>
          </Grid>
          <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Reasons
          </Typography>
          {EXPIRED_CANCELLED.reasons.map((r) => (
            <div
              key={r.reason}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 0',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '0.8rem',
                color: '#555',
              }}
            >
              <span>{r.reason}</span>
              <Chip label={r.count} size="small" style={{ height: 20, fontSize: '0.7rem' }} />
            </div>
          ))}
        </Paper>
      </Paper>
    </div>
  );
};

export default ReferralMetrics;
